// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import { ClickableProps } from "@insite/mobius/Clickable";
import { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Check from "@insite/mobius/Icons/Check";
import RotateCcw from "@insite/mobius/Icons/RotateCcw";
import { Clickable, Icon, TextField, VisuallyHidden } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import Close from "@store/Library/Components/Toolkit/Icons/Close";
import Card, { CardStyles } from "@store/Library/Widgets/Card/Card";
import CardBody, { CardBodyStyles } from "@store/Library/Widgets/Card/CardBody";
import CardFooter, { CardFooterStyles } from "@store/Library/Widgets/Card/CardFooter";
import CardHeader, { CardHeaderStyles } from "@store/Library/Widgets/Card/CardHeader";
import { ManageDefaultPOEditingRowProps } from "@store/Library/Widgets/ManageDefaultPO/ManageDefaultPOEditingRow";
import useEditManageDefaultPO from "@store/Library/Widgets/ManageDefaultPO/useEditManageDefaultPO";
import DatePicker from "@store/MobiusOverrides/DatePicker/DatePicker";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React from "react";
import styled, { css } from "styled-components";

export const HorizontalRule = styled.hr`
    margin: 0.75rem 0;
    border: 1px solid rgba(74, 74, 74, 0.2);
`;
export interface ManageDefaultPOEditingCardStyles {
    card?: CardStyles;
    cardHeader?: CardHeaderStyles;
    cardBody?: CardBodyStyles;
    datePicker?: DatePickerPresentationProps;
    cardFooter?: CardFooterStyles;
    confirmClickable?: ClickableProps;
    savePoButton?: IconPresentationProps;
    cancelChangeButton?: IconPresentationProps;
}
export const manageDefaultPOEditingCardStyles: ManageDefaultPOEditingCardStyles = {
    cancelChangeButton: {
        src: RotateCcw,
        size: 30,
        css: css`
            > svg {
                stroke-width: 2;
            }
            color: ${getColor("primary")};
            :hover,
            :focus {
                color: ${getColor("linkDark")};
            }
        `,
    },
    savePoButton: {
        src: Check,
        size: 30,
        css: css`
            > svg {
                stroke-width: 2;
            }
            color: ${getColor("primary")};
            :hover,
            :focus {
                color: ${getColor("linkDark")};
            }
        `,
    },
    confirmClickable: {
        css: css`
            :disabled {
                cursor: not-allowed;
                opacity: 0.4;
            }
        `,
    },
    card: {
        css: css`
            width: 100%;
            border-radius: 0.75rem;
            border: 1.5px solid ${getColor("common.backgroundColorLightWarmGrey")};
        `,
    },
    cardBody: {
        css: css`
            display: flex;
            justify-content: space-between;
            gap: 1.5rem;
        `,
    },
};
export interface ManageDefaultPOEditingCardProps
    extends Omit<ManageDefaultPOEditingRowProps, "extendedStyles" | "overrideStyles"> {
    extendedStyles?: ManageDefaultPOEditingCardStyles;
    overrideStyles?: ManageDefaultPOEditingCardStyles;
}

const ManageDefaultPOEditingCard: React.FC<ManageDefaultPOEditingCardProps> = ({
    customerPoNumber,
    extendedStyles,
    overrideStyles,
    onSave,
    onCancel,
    validateStartDate,
    validateEndDate,
    isNew = false,
}) => {
    const styles = useExpandProps(manageDefaultPOEditingCardStyles, extendedStyles, overrideStyles);
    const {
        poNumber,
        setPoNumber,
        startDate,
        endDate,
        handleStartDateChange,
        handleEndDateChange,
        handleSubmit,
        isValidSubmit,
        hasSubmitted,
    } = useEditManageDefaultPO(customerPoNumber, onSave, validateEndDate, validateStartDate);
    return (
        <Card size="small" align="" justify="center" extendedStyles={styles.card}>
            <CardHeader {...styles.cardHeader}>
                <TextField
                    label={translate("PO Number")}
                    sizeVariant="small"
                    value={poNumber}
                    error={hasSubmitted ? !poNumber : false}
                    onChange={event => setPoNumber(event.currentTarget.value)}
                />
            </CardHeader>

            <CardBody extendedStyles={styles.cardBody}>
                <DatePicker
                    sizeVariant="small"
                    error={hasSubmitted ? !validateStartDate(startDate, endDate || null) : false}
                    label={translate("Start Date")}
                    onDayChange={handleStartDateChange}
                    selectedDay={startDate}
                    dateTimePickerProps={{
                        clearIcon: null,
                        maxDate: endDate,
                    }}
                    {...styles.datePicker}
                />
                <DatePicker
                    sizeVariant="small"
                    error={hasSubmitted ? !validateEndDate(startDate, endDate || null) : false}
                    onDayChange={handleEndDateChange}
                    selectedDay={endDate || undefined}
                    label={translate("End Date")}
                    dateTimePickerProps={{
                        clearIcon: null,
                        minDate: startDate,
                    }}
                    {...styles.datePicker}
                />
            </CardBody>
            <HorizontalRule />
            <CardFooter justifyContent="space-around">
                <Clickable
                    disabled={hasSubmitted ? !isValidSubmit : false}
                    onClick={handleSubmit}
                    data-test-selector="PoOverride_savePoButton"
                    {...styles.confirmClickable}
                >
                    <Icon {...styles.savePoButton} />
                    <VisuallyHidden>{translate("save changes")}</VisuallyHidden>
                </Clickable>
                <Clickable onClick={onCancel} data-test-selector="PoOverride_cancelPoButton">
                    <Icon {...styles.cancelChangeButton} src={isNew ? Close : RotateCcw} />
                    <VisuallyHidden>{translate("Cancel this Edit")}</VisuallyHidden>
                </Clickable>
            </CardFooter>
        </Card>
    );
};

export default ManageDefaultPOEditingCard;
